import {
  DEFAULT_PROVIDER,
  LabelProvider,
  external_provider_of,
} from "./provider";
import { reactive, ref } from "@vue/composition-api";
import { MipCommonState, MipLabel } from "./model";
import * as api from "./api";
import { SharedState } from "@/common/state";
import { create_counter, create_tree_by, emptyFn, once } from "@/fp";
import "./mock/api.mock";
import { org_settings } from "@/common/api/user-settings.api";
import { UseModal } from "@/components/root/modal";
import ErrorModal from "@/components/modals/ErrorModal.vue";
const MipSettings = org_settings("mip", { added: false, on: false });

const { in_motion_services_flags } = SharedState;

const state = reactive({
  labels_config: {},
  /**
   * Which provider the tabs are currently on. Read-only display state for the
   * copy and the label column; on the write side it selects the
   * `externalLabelProvider` every integrations-manager call carries.
   */
  provider: DEFAULT_PROVIDER,
  google_labels: [] as MipLabel[],
  mipLabelsTree: {},
  only_native_mip_labels_tree: {},
  isMipActivated: false,
  indexed_mip_labels: {},
  mappable_label_ids: new Set<string>(),
} as MipCommonState);

const has_setup_status_known = ref(false);

const get_mip_labels = async (): Promise<{
  all: MipLabel[][];
  native: MipLabel[][];
}> => {
  const res = await api.getMipLabelsTree("microsoft");
  if (res.success) {
    const { labels, is_empty /* , missing */ } = res.value;
    if (is_empty) {
      return { all: [], native: [] };
    } else {
      // to be used for setting the parent name on each label.
      const indexed_mip_names = labels.reduce((acc, item) => {
        acc[item.labelId] = item.labelName;
        return acc;
      }, {} as Record<string, string>);
      const make_tree = create_tree_by<MipLabel>("labelId", "parentId");
      const only_mip_labels = labels.filter(
        (l: MipLabel) => !SharedState.getInfoType(l.mappedLabelId)
      );
      const labels_with_parent_names_set = labels.map((n) => {
        n.parentName = indexed_mip_names[n.parentId] || "";
        return n;
      });
      // @ts-ignore
      const tree = make_tree(labels_with_parent_names_set).map((item) =>
        item.flat(1)
      );

      const only_native_mip_labels_tree = make_tree(
        only_mip_labels
      ).map((item) => item.flat(1));

      return { all: tree, native: only_native_mip_labels_tree };
    }
  } else {
    const { code, body = {} } = res;
    if (code == 424) {
      UseModal().show(ErrorModal, {
        ...body,
        title: "Loading of MIP labels failed",
        scope: `We couldn't load your labels upon request.<br/>
        External system <b><i>${body.resource}</i></b> reported: <br/>
        <b>${body.message}</b>`,
      });
    }
  }
  return { all: [], native: [] };
};

const update_override_all_status_for_label = async (
  label_id: string,
  override: boolean
) => {
  let res;
  const payload = {
    labelId: label_id,
    externalLabelProvider: external_provider_of(state.provider),
  };
  if (override) {
    res = await api.overide_all_for_label(payload);
  } else {
    res = await api.overide_none_for_label(payload);
  }
  if (res.success) {
    state.labels_config = {
      ...state.labels_config,
      [label_id]: {
        ...state.labels_config[label_id],
        override,
      },
    };
  }
};

const mutate_override_all_status = (label_id: string, override: boolean) => {
  state.labels_config = {
    ...state.labels_config,
    [label_id]: {
      ...state.labels_config[label_id],
      override,
    },
  };
};

const get_is_label_global_override = (label_id: string): boolean => {
  // @ts-ignore
  const label = state.labels_config[label_id];
  if (label) {
    return label.override as boolean;
  } else {
    return false;
  }
};

const callbacks: Record<number, Function> = {};
const next_id = create_counter(1);
let is_ready = false;
const on_ready = (cb: Function): Function => {
  if (is_ready) {
    cb();
    return emptyFn();
  } else {
    const id = next_id();
    callbacks[id] = cb;
    callbacks;
    return () => {
      delete callbacks[id];
    };
  }
};

let is_mip_activated = false;

const get_is_mip_activated = async () => {
  if (SharedState.env.value.env === "uat") {
    return true;
  }
  const res = await MipSettings.get();
  is_mip_activated = res.added && res.on;
  return is_mip_activated;
};

/**
 * The rollout switch for Google Drive labeling. It gates the Configurations
 * entry, this screen's Google route, and the label fetch below. With it off
 * nothing here differs from the Microsoft-only screen that existed before.
 */
const is_gdrive_labeling_enabled = () =>
  SharedState.FeatureHelper.is_enabled("auto_gdrive_labeling");

/**
 * Google Drive labeling has no consent step of its own: it rides the access
 * the org already granted for Google in motion. It must not inherit
 * Microsoft's, or an org that never turned MIP on could never reach the Google
 * screen even with the flag enabled.
 */
const is_google_labeling_available = () =>
  is_gdrive_labeling_enabled() &&
  !in_motion_services_flags.value.is_ms_org_only;

/** whether the screen for `provider` has anything to show */
const is_provider_ready = (provider: LabelProvider) =>
  provider === "google" ? is_google_labeling_available() : state.isMipActivated;

const set_provider = (provider: LabelProvider) => {
  state.provider = provider;
};

/**
 * Both providers' label lists are fetched here, once, so moving between the
 * Microsoft and Google screens is display state with no async work. An earlier
 * version refetched per provider and left the previous provider's labels on
 * screen until a manual page refresh.
 */
const init_common_state = once(async () => {
  state.isMipActivated = await get_is_mip_activated();
  const google_available = is_google_labeling_available();
  has_setup_status_known.value = true;

  if (state.isMipActivated) {
    const { all, native } = await get_mip_labels();
    state.mipLabelsTree = all;
    state.only_native_mip_labels_tree = native;

    state.indexed_mip_labels = all.flat(1).reduce((acc, label) => {
      acc[label.labelId] = { ...label, service_type: "ms" };
      return acc;
    }, {} as Record<string, MipLabel>);
  }

  if (google_available) {
    const google_res = await api.getMipLabelsTree("google");
    if (google_res.success) {
      state.google_labels = google_res.value.labels;
      // indexed alongside the Microsoft labels so a mapping row can resolve its
      // label without knowing which provider produced it
      state.google_labels.forEach((l) => {
        state.indexed_mip_labels[l.labelId] = {
          ...l,
          service_type: "google",
        };
      });
    }
  }

  if (state.isMipActivated || google_available) {
    const res = await api.getCognniLabelsConfigs();
    if (res.success) {
      state.labels_config = res.value.reduce((acc, l) => {
        // @ts-ignore
        acc[l.labelId] = l;
        return acc;
      }, {});
    }
    const mappable_res = await api.get_mappable_labels();
    if (mappable_res.success && mappable_res.value.length > 0) {
      state.mappable_label_ids = new Set(
        mappable_res.value.map((l) => l.labelId)
      );
    }
    is_ready = true;
    Object.values(callbacks).forEach((cb) => cb());
  }
});

export const common_state = {
  state,
  on_ready,
  init_common_state,
  has_setup_status_known,
  update_override_all_status_for_label,
  mutate_override_all_status,
  get_is_label_global_override,
  set_provider,
  is_provider_ready,
};
